import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
    EventEmitter,
    Inject,
    Optional,
} from '@angular/core';
import { BecomeVisibleDirective } from '@fi-shared/fi-directives/become-visible/become-visible.directive';
import { ObjectTypes } from '@fi-shared/types';
import { FiNewsFeedSharedService } from '@fi-shared/fi-news-feed-shared/fi-news-feed-shared.service';
import { IObjectDto } from '@fi-shared/models/models';
import { INotificationCategoryItem } from '@fi-shared/fi-notification/fi-notification';
import { FiNotificationHelperService } from '@fi-shared/fi-notification/fi-notification-helper.service';
import { VISIBILITY_HELPER, VisibilityHelper } from '@fi-shared/fi-directives/become-visible/visibility-helper';

interface NewsFeedVisitableItem extends IObjectDto, INotificationCategoryItem {}

export interface INewsFeedVisibleArgs {
    isLongPost?: boolean;
}

@Component({
    selector: 'fi-news-feed-visibility-helper',
    template: ``,
    styleUrls: ['./fi-news-feed-visibility-helper.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-news-feed-visibility-helper' },
    standalone: false,
})
export class FiNewsFeedVisibilityHelperComponent implements OnInit, OnDestroy {
    @Input() fiBecomeVisible: BecomeVisibleDirective;
    @Input() newsFeedItem: NewsFeedVisitableItem;
    @Input() newsFeedElement: HTMLElement;
    @Output() newsFeedMarkedAsVisited = new EventEmitter<void>();

    private fullContentVisible: boolean;
    private fiVisibilityTimeout: NodeJS.Timeout;
    private removeElementFromCheckVisibilityQueueFn: () => void;

    constructor(
        private newsFeedSharedService: FiNewsFeedSharedService,
        private notificationHelper: FiNotificationHelperService,
        @Inject(VISIBILITY_HELPER) @Optional() private visibilityHelper?: VisibilityHelper,
    ) {}

    ngOnInit(): void {
        if (this.visibilityHelper && this.notificationHelper.isNotVisited(this.newsFeedItem)) {
            this.removeElementFromCheckVisibilityQueueFn = this.visibilityHelper.addElementToCheckVisibilityQueue(
                this.newsFeedElement,
                (isVisible) => {
                    if (isVisible) {
                        this.newsItemBecomeVisible();
                    } else {
                        this.newsItemBecomeInvisible();
                    }
                },
            );
        }
    }

    onNewsItemContentBecomeVisible(args?: INewsFeedVisibleArgs) {
        // https://chainformation.atlassian.net/browse/FI-2873
        // fires when post has short content or when user clicks 'show more'
        this.fullContentVisible = true;
        if (!this.fiBecomeVisible || this.fiBecomeVisible.isVisible()) {
            this.startVisibilityCheck(args);
        }
    }

    onNewsItemContentBecomeInvisible() {
        this.fullContentVisible = false;
        this.newsItemBecomeInvisible();
    }

    newsItemBecomeVisible() {
        if (this.isAutoVisited()) {
            this.startVisibilityCheck();
        }
    }

    newsItemBecomeInvisible() {
        this.clearVisibilityTimeout();
    }

    private canHaveShowMore() {
        return (
            this.newsFeedItem.ObjectType === ObjectTypes.Post || this.newsFeedItem.ObjectType === ObjectTypes.ForumPost
        );
    }
    private isAutoVisited() {
        return !this.canHaveShowMore() || this.fullContentVisible;
    }

    private clearVisibilityTimeout() {
        if (this.fiVisibilityTimeout) {
            clearTimeout(this.fiVisibilityTimeout);
            this.fiVisibilityTimeout = undefined;
        }
    }

    private startVisibilityCheck(args?: INewsFeedVisibleArgs) {
        // https://chainformation.atlassian.net/browse/FI-5778
        const visibilityInterval = args?.isLongPost ? 3000 : 1500;

        if (!this.fiVisibilityTimeout && this.notificationHelper.isNotVisited(this.newsFeedItem)) {
            this.fiVisibilityTimeout = setTimeout(() => {
                const objectItems = this.newsFeedSharedService.getObjectItems([this.newsFeedItem]);
                this.newsFeedSharedService.markNewsFeedsAsVisited(objectItems).then(() => {
                    this.newsFeedMarkedAsVisited.emit();
                });
                if (this.removeElementFromCheckVisibilityQueueFn) {
                    this.removeElementFromCheckVisibilityQueueFn();
                }
            }, visibilityInterval);
        }
    }

    ngOnDestroy(): void {
        this.clearVisibilityTimeout();
        if (this.removeElementFromCheckVisibilityQueueFn) {
            this.removeElementFromCheckVisibilityQueueFn();
        }
    }
}
